import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Role } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateRole, useUpdateRole } from '../hooks/use-role-mutations';
import {
  emptyRoleForm,
  fromRole,
  roleFormSchema,
  toRoleCreate,
  toRoleUpdate,
  type RoleFormValues,
} from '../schemas/role-form-schema';
import { roleErrorTarget, roleKeyFrom } from '../schemas/role-rules';

import { PermissionChecklist } from './permission-checklist';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['name', 'key', 'permissions']);
const mapField = (field: string) => field.replace(/\.\d+$/, '');

export interface RoleDialogProps {
  /** null creates; a custom role edits it (its key stays). */
  role: Role | null;
  /** Permissions of the signed-in person; the checklist cannot go beyond them. */
  held: readonly string[];
  /** Starting point for a new role, e.g. a system role to copy. */
  template?: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoleDialog({ role, held, template = null, open, onOpenChange }: RoleDialogProps) {
  const { t } = useTranslation(['team', 'common']);
  const message = useErrorMessage();
  const create = useCreateRole();
  const update = useUpdateRole();
  const [keyTouched, setKeyTouched] = useState(Boolean(role));
  const form = useForm<RoleFormValues>({
    resolver: useI18nResolver(roleFormSchema),
    defaultValues: role
      ? fromRole(role)
      : template
        ? { name: '', key: '', permissions: template.permissions.filter((p) => p !== '*') }
        : emptyRoleForm(),
  });
  const isPending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      if (role) await update.mutateAsync({ id: role.id, body: toRoleUpdate(values) });
      else await create.mutateAsync(toRoleCreate(values));
      close();
    } catch (error) {
      const target = roleErrorTarget(error);
      if (target) {
        form.setError(target.field, { type: 'server', message: t(target.key, target.values) });
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {role ? t('team:roles.edit.title', { name: role.name }) : t('team:roles.create.title')}
          </DialogTitle>
          <DialogDescription>{t('team:roles.create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('team:roles.fields.name')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        placeholder="Stripe finance"
                        {...field}
                        onChange={(event) => {
                          field.onChange(event);
                          if (!keyTouched) {
                            form.setValue('key', roleKeyFrom(event.target.value), {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('team:roles.fields.key')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        className="font-mono text-[13px]"
                        readOnly={Boolean(role)}
                        {...field}
                        onChange={(event) => {
                          setKeyTouched(true);
                          field.onChange(event);
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      {role ? t('team:roles.fields.keyFixed') : t('team:roles.fields.keyHint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="permissions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:roles.fields.permissions')}</FormLabel>
                  <FormDescription>{t('team:roles.fields.permissionsHint')}</FormDescription>
                  <PermissionChecklist value={field.value} onChange={field.onChange} held={held} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : role
                    ? t('common:actions.save')
                    : t('team:roles.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
